import {
  EMPTY_COMMUNICATION_ROLLUP,
  type CommunicationHistoryItem,
  type CommunicationHistoryRecipient,
  type CommunicationRecipientStatus,
  type CommunicationRollup,
} from './communication-types';
import type { EmailTemplate, SentEmail } from './types';

export const COMMUNICATION_HISTORY_LIMIT = 50;

const TEMPLATE_ALIASES: ReadonlySet<string> = new Set([
  'call-sheet',
  'call-sheet-digest',
  'rehearsal-report',
  'meeting-report',
  'line-notes',
  'deadline-ping',
  'meeting-invitation',
  'project-invite',
  'custom-announcement',
  'schedule-change',
  'deadline-approaching',
  'welcome-onboarding',
  'profile-reminder',
  'mention-notification',
]);

function stringField(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function numberField(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function objectField(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function typedObjectField<T extends object>(value: unknown): T | null {
  const record = objectField(value);
  return Object.keys(record).length > 0 ? record as T : null;
}

function templateAlias(value: unknown): EmailTemplate {
  const alias = stringField(value, 'custom-announcement');
  return TEMPLATE_ALIASES.has(alias) ? alias as EmailTemplate : 'custom-announcement';
}

function origin(value: unknown): CommunicationHistoryItem['origin'] {
  return value === 'automated' || value === 'system' ? value : 'manual';
}

function recipientStatus(value: unknown): CommunicationRecipientStatus {
  if (
    value === 'intended'
    || value === 'suppressed'
    || value === 'accepted'
    || value === 'delivered'
    || value === 'first-opened'
    || value === 'bounced'
    || value === 'spam-complaint'
    || value === 'failed'
  ) {
    return value;
  }

  return 'intended';
}

function normalizeRollup(value: unknown, recipients: CommunicationHistoryRecipient[]): CommunicationRollup {
  const rollup = objectField(value);
  if (Object.keys(rollup).length > 0) {
    const normalized = {
      intended: numberField(rollup.intended),
      accepted: numberField(rollup.accepted),
      delivered: numberField(rollup.delivered),
      firstOpened: numberField(rollup.firstOpened),
      bounced: numberField(rollup.bounced),
      complained: numberField(rollup.complained),
      suppressed: numberField(rollup.suppressed),
      failed: numberField(rollup.failed),
      attention: numberField(rollup.attention),
    };
    normalized.attention ||= normalized.bounced + normalized.complained + normalized.suppressed + normalized.failed;
    return normalized;
  }

  return recipients.reduce<CommunicationRollup>((acc, recipient) => {
    acc.intended += 1;
    if (recipient.status === 'accepted') acc.accepted += 1;
    if (recipient.status === 'delivered') acc.delivered += 1;
    if (recipient.status === 'first-opened') acc.firstOpened += 1;
    if (recipient.status === 'bounced') acc.bounced += 1;
    if (recipient.status === 'spam-complaint') acc.complained += 1;
    if (recipient.status === 'suppressed') acc.suppressed += 1;
    if (recipient.status === 'failed') acc.failed += 1;
    acc.attention = acc.bounced + acc.complained + acc.suppressed + acc.failed;
    return acc;
  }, { ...EMPTY_COMMUNICATION_ROLLUP });
}

function issueSummary(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value;
  }
  const issue = objectField(value);
  const detail = stringField(issue.detail);
  const reason = stringField(issue.reason);
  return detail || reason || undefined;
}

export function communicationHistoryRecipientFromRecord(
  id: string,
  data: Record<string, unknown>,
): CommunicationHistoryRecipient {
  const displayName = stringField(data.displayName, stringField(data.name, stringField(data.email, 'Recipient')));
  const latestIssue = objectField(data.latestIssue);
  return {
    id,
    communicationId: stringField(data.communicationId),
    email: stringField(data.email),
    displayName,
    greeting: stringField(data.greeting) || undefined,
    status: recipientStatus(data.status),
    firstOpenedAt: stringField(data.firstOpenedAt) || undefined,
    deliveredAt: stringField(data.deliveredAt) || undefined,
    latestIssue: issueSummary(data.latestIssue),
    hasActiveIssue: data.hasActiveIssue === true || latestIssue.status === 'active',
    personnelId: stringField(data.personnelId) || undefined,
    sourceLabels: Array.isArray(data.sourceLabels)
      ? data.sourceLabels.filter((label): label is string => typeof label === 'string')
      : undefined,
  };
}

export function communicationHistoryItemFromRecord(
  id: string,
  data: Record<string, unknown>,
  recipients: CommunicationHistoryRecipient[],
): CommunicationHistoryItem {
  const metadata = objectField(data.metadata);
  return {
    id,
    templateAlias: templateAlias(data.templateAlias),
    subject: stringField(data.subject, 'Communication'),
    origin: origin(data.origin),
    sentAt: stringField(data.sentAt, stringField(data.createdAt)),
    sentBy: stringField(data.sentBy, stringField(data.createdBy)) || undefined,
    rollup: normalizeRollup(data.deliveryRollup ?? data.rollup, recipients),
    audienceSummary: stringField(metadata.audienceSummary) || undefined,
    audienceSelection: typedObjectField<NonNullable<CommunicationHistoryItem['audienceSelection']>>(
      data.audienceSelection
    ),
    recipientSnapshot: typedObjectField<NonNullable<CommunicationHistoryItem['recipientSnapshot']>>(
      data.recipientSnapshot
    ),
    linkedSource: typedObjectField<NonNullable<CommunicationHistoryItem['linkedSource']>>(data.linkedSource),
    recipients,
  };
}

export function sentEmailToCommunicationHistoryItem(email: SentEmail): CommunicationHistoryItem {
  const sent = numberField(email.sent);
  const total = numberField(email.total, numberField(email.recipientCount));
  return {
    id: email.id,
    templateAlias: templateAlias(email.templateAlias),
    subject: stringField(email.subject, 'Email'),
    origin: email.sentBy === 'system' ? 'automated' : 'manual',
    sentAt: stringField(email.sentAt),
    sentBy: stringField(email.sentBy) || undefined,
    rollup: {
      ...EMPTY_COMMUNICATION_ROLLUP,
      intended: total,
      accepted: sent,
      failed: Math.max(0, total - sent),
      attention: Math.max(0, total - sent),
    },
    audienceSelection: email.audienceSelection ?? null,
    recipientSnapshot: email.recipientSnapshot ?? null,
    linkedSource: email.linkedSource ?? null,
    recipients: [],
    summaryOnly: true,
  };
}

function sentAtTime(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function mergeCommunicationHistory(items: CommunicationHistoryItem[]): CommunicationHistoryItem[] {
  return items
    .toSorted((left, right) => {
      const delta = sentAtTime(right.sentAt) - sentAtTime(left.sentAt);
      return delta || right.sentAt.localeCompare(left.sentAt);
    })
    .slice(0, COMMUNICATION_HISTORY_LIMIT);
}
